'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './MessageAttachment.module.css';
import { Card } from './Card';

export interface MessageAttachmentProps extends React.HTMLAttributes<HTMLDivElement> {
  /** File URL — image, PDF, or any downloadable resource. */
  url: string;
  /** IANA media type — drives layout selection. */
  mediaType: string;
  /** Display name. */
  filename: string;
  /** Subtitle text under the filename (defaults to a humanised mediaType). */
  metaText?: string;
  /** When `true`, switches to high-contrast styling for placement on dark/coloured bubbles. */
  onDark?: boolean;
  /** Override the inferred icon. */
  icon?: React.ReactNode;
  className?: string;
}

function formatMime(mediaType: string): string {
  if (mediaType.startsWith('image/')) return 'Image';
  if (mediaType === 'application/pdf') return 'PDF';
  if (
    mediaType === 'application/msword' ||
    mediaType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'Document';
  }
  if (mediaType.startsWith('text/') || mediaType === 'application/json') return 'Text';
  return mediaType;
}

function FileIcon({ mediaType }: { mediaType: string }) {
  if (mediaType === 'application/pdf') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </svg>
    );
  }
  if (
    mediaType === 'application/msword' ||
    mediaType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
        <path d="M9 13l1.5 4 1.5-3 1.5 3L15 13" />
      </svg>
    );
  }
  if (mediaType.startsWith('text/') || mediaType === 'application/json') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function ChevronUp() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}
function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
function ExternalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

/**
 * MessageAttachment
 *
 * Inline file renderer for chat messages.
 *
 * Layout rules:
 *  - `image/*` → click-to-open thumbnail
 *  - `application/pdf` → header chip + collapsible inline `<object>`
 *  - text-types → header chip + collapsible inline `<pre>` (loaded on demand)
 *  - everything else → header chip + open-in-new link
 */
export const MessageAttachment = React.forwardRef<HTMLDivElement, MessageAttachmentProps>(
  ({ url, mediaType, filename, metaText, onDark = false, icon, className, ...rest }, ref) => {
    const isImage = mediaType.startsWith('image/');
    const isPdf = mediaType === 'application/pdf';
    const isText = mediaType.startsWith('text/') || mediaType === 'application/json';

    const [expanded, setExpanded] = React.useState(false);
    const [textPreview, setTextPreview] = React.useState<string | null>(null);
    const [textError, setTextError] = React.useState<string | null>(null);

    const loadTextPreview = React.useCallback(async () => {
      setExpanded((v) => !v);
      if (textPreview !== null || textError !== null) return;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          setTextError(`Could not load (${res.status})`);
          return;
        }
        const txt = await res.text();
        setTextPreview(txt.length > 8000 ? `${txt.slice(0, 8000)}\n…` : txt);
      } catch (err) {
        setTextError(err instanceof Error ? err.message : 'Could not load');
      }
    }, [url, textPreview, textError]);

    if (isImage) {
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={clsx(styles.imageLink, className)}
          aria-label={`Open ${filename}`}
        >
          <img src={url} alt={filename} loading="lazy" className={styles.image} />
        </a>
      );
    }

    return (
      <Card
        ref={ref}
        variant="outlined"
        padding="sm"
        className={clsx(styles.fileCard, className)}
        data-on-dark={onDark ? 'true' : undefined}
        {...rest}
      >
        <div className={styles.fileRow}>
          <span className={styles.iconWrap}>{icon ?? <FileIcon mediaType={mediaType} />}</span>
          <div className={styles.body}>
            <span className={styles.filename} title={filename}>
              {filename}
            </span>
            <span className={styles.meta}>{metaText ?? formatMime(mediaType)}</span>
          </div>
          <span className={styles.actions}>
            {(isPdf || isText) && (
              <button
                type="button"
                onClick={isText ? loadTextPreview : () => setExpanded((v) => !v)}
                className={styles.iconButton}
                aria-label={expanded ? 'Collapse preview' : 'Expand preview'}
                aria-expanded={expanded}
              >
                {expanded ? <ChevronUp /> : <ChevronDown />}
              </button>
            )}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.iconButton}
              aria-label={`Open ${filename} in a new tab`}
            >
              <ExternalIcon />
            </a>
          </span>
        </div>

        {isPdf && expanded && (
          <object data={url} type="application/pdf" className={styles.pdfPreview}>
            <span>
              PDF preview unsupported in this browser.{' '}
              <a href={url} target="_blank" rel="noopener noreferrer">
                Open
              </a>
              .
            </span>
          </object>
        )}

        {isText && expanded && (
          <pre className={styles.textPreview}>
            {textError ? textError : textPreview === null ? 'Loading…' : textPreview}
          </pre>
        )}
      </Card>
    );
  }
);

MessageAttachment.displayName = 'MessageAttachment';

export default MessageAttachment;
